import { BuildCancelledError } from "@map2threejs/core/errors";

export type ProviderFailureKind = "authentication" | "permission" | "coverage" | "rate-limit" | "server" | "network";

export class ProviderRequestError extends Error {
  readonly status: number | undefined;
  readonly kind: ProviderFailureKind;

  constructor(message: string, kind: ProviderFailureKind, status?: number) {
    super(message);
    this.name = "ProviderRequestError";
    this.kind = kind;
    this.status = status;
  }
}

function failureForStatus(status: number, label: string): ProviderRequestError {
  if (status === 401) return new ProviderRequestError(`${label} rejected the API key (401).`, "authentication", status);
  if (status === 403) return new ProviderRequestError(`${label} is not permitted for this API key (403).`, "permission", status);
  if (status === 404 || status === 204 || status === 400) {
    return new ProviderRequestError(`${label} is unavailable for this location or source (${status}).`, "coverage", status);
  }
  if (status === 429) return new ProviderRequestError(`${label} is temporarily rate limited (429).`, "rate-limit", status);
  if (status >= 500) return new ProviderRequestError(`${label} is temporarily unavailable (${status}).`, "server", status);
  return new ProviderRequestError(`${label} failed with HTTP ${status}.`, "network", status);
}

function cancellableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new BuildCancelledError());
      return;
    }
    const complete = (): void => {
      signal.removeEventListener("abort", cancel);
      resolve();
    };
    const timer = globalThis.setTimeout(complete, milliseconds);
    const cancel = (): void => {
      globalThis.clearTimeout(timer);
      reject(new BuildCancelledError());
    };
    signal.addEventListener(
      "abort",
      cancel,
      { once: true },
    );
  });
}

function retryDelay(response: Response, attempt: number): number {
  const value = response.headers.get("retry-after");
  if (value) {
    const seconds = Number(value);
    if (Number.isFinite(seconds)) return Math.min(10_000, Math.max(0, seconds * 1000));
    const date = Date.parse(value);
    if (Number.isFinite(date)) return Math.min(10_000, Math.max(0, date - Date.now()));
  }
  return attempt === 0 ? 300 : 900;
}

export class RequestScheduler {
  private active = 0;
  private readonly queue: Array<() => void> = [];

  constructor(
    private readonly limit = 6,
    private readonly timeoutMilliseconds = 20_000,
  ) {}

  private async fetchWithTimeout(url: string, signal: AbortSignal): Promise<Response> {
    const controller = new AbortController();
    const forwardAbort = (): void => controller.abort();
    signal.addEventListener("abort", forwardAbort, { once: true });
    const timeout = globalThis.setTimeout(() => controller.abort(), this.timeoutMilliseconds);
    try {
      return await globalThis.fetch(url, {
        signal: controller.signal,
        credentials: "omit",
        cache: "default",
        referrerPolicy: "strict-origin-when-cross-origin",
      });
    } finally {
      globalThis.clearTimeout(timeout);
      signal.removeEventListener("abort", forwardAbort);
    }
  }

  private async acquire(signal: AbortSignal): Promise<void> {
    if (signal.aborted) throw new BuildCancelledError();
    if (this.active < this.limit) {
      this.active += 1;
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const start = (): void => {
        signal.removeEventListener("abort", cancel);
        this.active += 1;
        resolve();
      };
      const cancel = (): void => {
        const index = this.queue.indexOf(start);
        if (index >= 0) this.queue.splice(index, 1);
        reject(new BuildCancelledError());
      };
      this.queue.push(start);
      signal.addEventListener("abort", cancel, { once: true });
    });
  }

  private release(): void {
    this.active = Math.max(0, this.active - 1);
    this.queue.shift()?.();
  }

  async fetch(url: string, label: string, signal: AbortSignal): Promise<Response> {
    await this.acquire(signal);
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (signal.aborted) throw new BuildCancelledError();
        try {
          const response = await this.fetchWithTimeout(url, signal);
          if (response.ok) return response;
          const failure = failureForStatus(response.status, label);
          if ((failure.kind === "rate-limit" || failure.kind === "server") && attempt < 2) {
            await cancellableDelay(retryDelay(response, attempt), signal);
            continue;
          }
          throw failure;
        } catch (error) {
          if (error instanceof BuildCancelledError || signal.aborted) throw new BuildCancelledError();
          if (error instanceof ProviderRequestError) throw error;
          if (attempt < 2) {
            await cancellableDelay(attempt === 0 ? 300 : 900, signal);
            continue;
          }
        }
      }
      throw new ProviderRequestError(
        `${label} could not be reached after three attempts.`,
        "network",
        undefined,
      );
    } finally {
      this.release();
    }
  }
}
